/* eslint-disable no-undef */
const { error, debug } = console;

/**
 * A single keyed cache that can be composed into a larger cache mechanism
 */
class LocalStorageCache {
  /**
   *Creates an instance of LocalStorageCache.
   *
   * @param {string} key cache key
   * @param {number} ttl time to live in seconds
   * @memberof LocalStorageCache
   */
  constructor(key, ttl = 86400) {
    Object.assign(this, { key, ttl });
    this._cachedPayload = undefined;
    this._cachedExp = undefined;
    this._subscribers = new Set();

    window.addEventListener("storage", (e) => {
      if (e.key === this.key) {
        this._cachedPayload = undefined;
        this._cachedExp = undefined;
        this._notify();
      }
    });
  }

  subscribe(subscribeFn) {
    this._subscribers.add(subscribeFn);
    return () => this._subscribers.delete(subscribeFn);
  }

  _notify() {
    for (const subscribeFn of this._subscribers) {
      subscribeFn();
    }
  }

  /**
   *
   *
   * @returns {object|undefined}
   * @memberof LocalStorageCache
   */
  get() {
    try {
      // Check if we have a cached payload that's still valid
      if (this._cachedPayload !== undefined && this._isCachedValid()) {
        debug(`cache hit for ${this.key}`);
        return this._cachedPayload;
      }

      // Otherwise, try to load from localStorage
      let cached = localStorage.getItem(this.key);
      cached = cached ? JSON.parse(cached) : undefined;

      if (!cached || this.isExpired(cached) || !cached.payload) {
        debug(`cache miss for ${this.key}`);
        this._cachedPayload = undefined;
        this._cachedExp = undefined;
        return;
      } else {
        debug(`cache hit for ${this.key}`);
        // Cache the payload and expiration for future calls
        this._cachedPayload = cached.payload;
        this._cachedExp = cached.exp;
        return this._cachedPayload;
      }
    } catch (error_) {
      error(error_);
      this._cachedPayload = undefined;
      this._cachedExp = undefined;
      return;
    }
  }

  _isCachedValid() {
    if (this._cachedExp === undefined) {
      return false;
    }
    // -1 means never expires
    if (this._cachedExp === -1) {
      return true;
    }
    // Check if expiration time is in the future
    return this._cachedExp >= this.nowInSeconds;
  }

  isExpired(cached) {
    // -1 means never expires
    if (cached.exp === -1) {
      return false;
    }
    // Expired if expiration time is in the past
    return cached.exp < this.nowInSeconds;
  }

  /**
   * @param {object} payload
   * @param {object} options.exp
   * @memberof LocalStorageCache
   */
  set(payload, { exp } = {}) {
    const expiration = exp || this.nowInSeconds + this.ttl;
    localStorage.setItem(
      this.key,
      JSON.stringify({
        exp: expiration,
        payload,
      })
    );
    this._cachedPayload = payload;
    this._cachedExp = expiration;
    this._notify();
  }

  remove() {
    localStorage.removeItem(this.key);
    this._cachedPayload = undefined;
    this._cachedExp = undefined;
    this._notify();
  }

  /**
   * @private
   */
  get nowInSeconds() {
    return Math.floor(Date.now() / 1000);
  }
}

export const uuid = () => {
  const url = URL.createObjectURL(new Blob());
  return url.slice(Math.max(0, url.lastIndexOf("/") + 1));
};

export const debounce = (callback, wait) => {
  let timeoutId;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
};

export default LocalStorageCache;
